<template>
    <div class="px-6 pt-6 pb-4">
        <router-link
            :to="{
                name: 'members-create',
                params: {
                    time: timestamp,
                },
            }"
            replace
            class="-ml-px relative inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-gray-50 hover:bg-gray-100 cursor-pointer float-right absolute"
        >
            <svg
                class="animate-spin h-6 w-6"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                v-if="loading"
            >
                <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                ></circle>
                <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
            </svg>
            <PlusIcon
                v-if="!loading"
                class="h-6 w-6 text-gray-600"
                aria-hidden="true"
            />
        </router-link>

        <h2 class="text-lg font-medium text-gray-900">
            {{ t("members.sidebar.title", "Ledenadministratie") }}
        </h2>

        <p
            class="mt-1 text-sm text-gray-600"
            v-if="members.length > 0 && total > 0"
        >
            {{ totalMembers() }}
        </p>
        <form class="mt-6 flex space-x-4">
            <div class="flex-1 min-w-0">
                <div
                    class="relative rounded-md shadow-sm"
                    v-if="searchOption == 'input'"
                >
                    <div
                        class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none"
                    >
                        <svg
                            class="h-5 w-5 text-gray-400"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            fill="currentColor"
                            aria-hidden="true"
                        >
                            <path
                                fill-rule="evenodd"
                                d="M8 4a4 4 0 100 8 4 4 0 000-8zM2 8a6 6 0 1110.89 3.476l4.817 4.817a1 1 0 01-1.414 1.414l-4.816-4.816A6 6 0 012 8z"
                                clip-rule="evenodd"
                            />
                        </svg>
                    </div>
                    <input
                        no-v-model="search"
                        @input="debounceSearch"
                        autocomplete="false"
                        spellcheck="false"
                        id="search"
                        type="search"
                        :disabled="loading"
                        class="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 sm:text-sm border-gray-300 rounded-md disabled:opacity-50"
                    />
                </div>

                <div
                    class="relative rounded-md shadow-sm"
                    v-if="searchOption == 'select' && options"
                >
                    <select
                        v-model="filter"
                        @change="switchSelect($event)"
                        class="block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                    >
                        <optgroup
                            :label="t('filter.' + optgroup)"
                            v-for="(options, optgroup) in options"
                            v-bind:key="optgroup"
                        >
                            <option
                                v-for="(option, value) in options"
                                v-bind:key="value"
                                :value="value"
                            >
                                {{ option }}
                            </option>
                        </optgroup>
                    </select>
                </div>
            </div>
            <a
                class="inline-flex justify-center px-3.5 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 cursor-pointer"
                @click="toggleSearcher()"
            >
                <svg
                    v-if="searchOption == 'input'"
                    class="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                    ></path>
                </svg>

                <svg
                    v-if="searchOption == 'select'"
                    class="h-5 w-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                >
                    <path
                        stroke-linecap="round"
                        stroke-linejoin="round"
                        stroke-width="2"
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                    ></path>
                </svg>
            </a>
        </form>
    </div>
    <nav class="flex-1 min-h-0 overflow-y-auto" aria-label="Directory">
        <div
            class="relative -mt-1"
            v-if="Object.keys(filteredMembers).length == 0 && !loading"
        >
            <info-box
                class="px-6"
                :message="t('members.no-results', 'Geen resultaten')"
            ></info-box>
        </div>

        <div
            class="relative"
            v-show="!loading"
            v-for="(membergroup, index) in filteredMembers"
            v-bind:key="index"
        >
            <div
                class="z-10 sticky top-0 border-t border-b border-gray-200 bg-gray-50 px-6 py-1 text-sm font-medium text-white bg-blue-700"
            >
                <h3>{{ index }}</h3>
            </div>
            <ul role="list" class="relative z-0 divide-y divide-gray-200">
                <li
                    v-for="(member, subindex) in membergroup"
                    v-bind:key="subindex"
                >
                    <div
                        class="relative px-6 flex items-center space-x-3 hover:bg-gray-50 focus-within:ring-2 focus-within:ring-inset focus-within:ring-blue-500"
                        :class="{
                            'border-l-4 border-blue-800 bg-gray-50':
                                member.checked,

                            'border-l-8 border-blue-400 bg-gray-100':
                                currentMember == member.hash,
                        }"
                    >
                        <div class="flex-shrink-0">
                            <router-link
                                :to="{
                                    name: 'members-edit',
                                    params: {
                                        id: member.hash,
                                        time: timestamp,
                                    },
                                }"
                                @click="currentMember = member.hash"
                                class="cursor-pointer block py-5"
                            >
                                <img
                                    class="h-10 w-10 rounded-full"
                                    loading="lazy"
                                    :alt="member.initials"
                                    v-if="member.avatar"
                                    :src="member.avatar"
                                />

                                <span
                                    class="inline-flex items-center justify-center h-10 w-10 rounded-full bg-blue-500"
                                    v-if="!member.avatar"
                                >
                                    <span
                                        class="font-medium leading-none text-white"
                                        >{{ member.initials }}</span
                                    >
                                </span>
                            </router-link>
                        </div>

                        <div class="flex-1 min-w-0">
                            <router-link
                                :to="{
                                    name: 'members-edit',
                                    params: {
                                        id: member.hash,
                                        time: timestamp,
                                    },
                                }"
                                replace
                                @click="currentMember = member.hash"
                                class="cursor-pointer text-blue-600 hover:text-blue-900 focus:outline-none block py-5"
                            >
                                <span
                                    class="absolute inset-y-0"
                                    aria-hidden="true"
                                ></span>
                                <p class="text-sm font-medium text-gray-900">
                                    {{ member.name }}
                                    {{ member.lastname }}
                                </p>
                                <div
                                    class="text-sm text-gray-500 truncate overflow-hidden"
                                >
                                    <div class="w-full overflow-x-auto">
                                        <span
                                            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-300 text-blue-900 ml-0.5"
                                            >{{
                                                t(
                                                    "meetings.tag.voting-weight",
                                                    "Stemgewicht:"
                                                )
                                            }}
                                            {{ member.voting_weight }}</span
                                        >

                                        <span
                                            class="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-800 ml-1"
                                            v-for="(tag, id) in member.tags"
                                            v-bind:key="id"
                                            >{{ tag }}</span
                                        >
                                    </div>
                                </div>
                            </router-link>
                        </div>

                        <div class="ml-2 flex items-center h-5 py-5">
                            <input
                                v-model="member.checked"
                                value="true"
                                type="checkbox"
                                class="focus:ring-blue-500 h-4 w-4 text-blue-600 border-gray-300 rounded cursor-pointer"
                            />
                        </div>
                    </div>
                </li>
            </ul>
        </div>
    </nav>
</template>

<script>
import InfoBox from "../../../components/InfoBox.vue";

import { PlusIcon } from "@heroicons/vue/outline";

export default {
    mounted() {
        this.getMembers();
        this.timestamp = new Date().getTime();
        Eventing.listen("fetchMembers", () => this.getMembers());

        Eventing.listen(
            "formMember",
            () => (this.timestamp = new Date().getTime())
        );

        Eventing.listen("fetchRoles", () => this.getFilterOptions());
    },
    components: {
        InfoBox,
        PlusIcon,
    },
    created() {},
    data() {
        return {
            members: [],
            loading: true,
            search: null,
            debounce: null,
            total: 0,
            timestamp: "ts",
            currentMember: false,
            filter: "active",
            activeFilter: null,
            options: null,
            searchOption: "input",
        };
    },
    methods: {
        switchSelect(event) {
            this.filter = event.target.value;
            this.activeFilter = this.filter;

            this.search = null;
            this.searchOption = "input";

            if (this.filter == "inactive") {
                this.getMembers();
                this.search = null;
                this.searchOption = "input";
            } else {
                this.getMembers();
            }
            if (this.filter) {
                this.search = this.filter;
            }
            // this.filter = null; // Weggehaald 7 april
        },
        toggleSearcher() {
            this.searchOption == "select"
                ? (this.searchOption = "input")
                : (this.searchOption = "select");

            if (this.searchOption == "select") {
                this.search = "filter";
                this.filter = "active";
            } else {
                this.filter = null;
                this.search = null;
            }

            this.getMembers();
        },
        getMembers() {
            this.loading = true;
            axios
                .get("/api/modules/members?filter=" + this.filter)
                .then((response) => {
                    this.members = response.data.members;
                    this.total = response.data.members.length;
                    // this.resetSearchState();
                    if (this.activeFilter == "inactive") {
                        this.search = null;
                    }
                    this.totalMembers();
                    this.getFilterOptions();
                    this.loading = false;
                })
                .catch((error) => {
                    alert(error.response.status);
                    // this.loading = "error";
                });
        },
        resetSearchState() {
            this.activeFilter = this.filter;
            this.filter = null;
            this.search = null;
            this.searchOption = "input";
        },
        debounceSearch(event) {
            clearTimeout(this.debounce);
            this.debounce = setTimeout(() => {
                this.search = event.target.value;
            }, 600);
        },
        getFilterOptions() {
            this.loading = true;
            axios
                .get("/api/modules/roles?options=filter")
                .then((response) => {
                    this.options = response.data;
                    this.loading = false;
                })
                .catch((error) => {
                    alert(error.response.status);
                    // this.loading = "error";
                });
        },
        totalMembers() {
            if (this.total) {
                var text = this.t(
                    "members.sidebar.subtitletotal",
                    "Zoek door :count leden"
                );
                text = text.replace(":count", this.total);
                return text;
            }
        },
    },
    computed: {
        filteredMembers() {
            var self = this;

            // this.activeFilter = this.filter;

            // if (this.search && this.filter == "inactive") {
            //     this.getMembers();
            //     return;
            // }
            if (this.filter && this.searchOption != "input") {
                this.search = this.filter;
                if (this.filter == "active") {
                    this.search = null;
                }
            }

            if (!this.search) return _.groupBy(this.members, "firstletter");
            var find = function (object, search) {
                for (var property in object) {
                    if (!["hash", "avatar", "id"].includes(property)) {
                        if (object.hasOwnProperty(property)) {
                            if (typeof object[property] == "object") {
                                find(object[property]);
                            } else if (
                                object[property].includes !== undefined
                            ) {
                                if (
                                    object[property]
                                        .toLowerCase()
                                        .includes(search)
                                )
                                    return true;
                            }
                        }
                    }
                }
                return false;
            };

            let members = this.members;

            members = members.filter((_item) => {
                return find(_item, this.search.toLowerCase());
            });

            return _.groupBy(members, "firstletter");
        },
        checkedList() {
            var self = this;
            try {
                if (this.members) {
                    return this.members.filter(function (u) {
                        return u.checked;
                    });
                }
            } catch (error) {}

            return null;
        },
    },
};
</script>
